import { Pencil, Plus } from 'lucide-react';
import { formatHumanDate, formatTail } from '@/lib/format.js';
import { money } from './rail-kit.jsx';
import { usePeekStore } from '@/stores/peek-store.js';
import { openContactPeek } from '@/lib/links.js';
import { aircraftLabel } from '@/lib/aircraftLabel.js';

// Cockpit V-B4 main-pane cards. Each card is a first-class surface (mockup
// cockpit-VB4-panes-focus.html) with its own edit affordance wired to the
// existing sheets. NamedInsuredCard + CoverageTargetsCard are new compact
// kv cards; aircraft/pilots/interests reuse their full panels unchanged.

// The card shell: an uppercase section header with an optional right-side
// affordance (Edit pencil or a labeled "+ Add" button), then content.
export function CockpitCard({ title, onEdit, addLabel, onAdd, children }) {
  return (
    <div className="rounded-card border border-border bg-surface px-4 py-3.5">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h2 className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-dim">{title}</h2>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1 rounded-[5px] border border-border px-2 py-1 text-[11px] text-text hover:border-border-strong hover:bg-surface-strong"
          >
            <Plus size={12} strokeWidth={1.5} />
            {addLabel || 'Add'}
          </button>
        ) : onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1 text-[11px] text-text-dim hover:text-text"
          >
            <Pencil size={11} strokeWidth={1.5} />
            Edit
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

// Compact two-column label/value row (mockup .kv). Suppresses empties. With
// onClick the value is a button (the primary row opens the person sheet).
function Row({ k, v, onClick }) {
  if (v == null || v === '' || v === '—') return null;
  return (
    <div className="grid grid-cols-[110px_1fr] items-baseline gap-x-2.5 gap-y-1 py-[3px]">
      <span className="text-[11px] text-text-dim">{k}</span>
      {onClick
        ? <button type="button" onClick={onClick} className="text-left text-[12.5px] font-medium text-text hover:underline">{v}</button>
        : <span className="text-[12.5px] font-medium text-text">{v}</span>}
    </div>
  );
}

export function NamedInsuredCard({ resolve, deal, onEdit }) {
  const name = resolve?.account_name || deal?.named_insured || '—';
  const entity = resolve?.policyholder_type || resolve?.account_type || deal?.Insurance_Line || null;
  const legal = entity ? `${name} · ${entity}` : name;
  const contactName = resolve?.primary_contact_name || deal?.primary_contact?.name || null;
  const phone = resolve?.phone || deal?.primary_contact?.phone || null;
  const email = resolve?.email || deal?.primary_contact?.email || null;
  const primary = [contactName, phone, email].filter(Boolean).join(' · ') || null;
  // The account's primary opens the person sheet (AR17), where his name, email
  // and phone are edited. A deal whose account names no primary shows the deal's
  // contact with no opener (the id is the account's, never the deal's guess).
  const openPeek = usePeekStore((s) => s.openPeek);
  const openPrimary = resolve?.primary_contact_id ? () => openPeek(openContactPeek(resolve.primary_contact_id)) : null;
  return (
    <CockpitCard title="Named insured" onEdit={onEdit}>
      <Row k="Legal name" v={legal} />
      <Row k="Primary" v={primary} onClick={openPrimary} />
    </CockpitCard>
  );
}

// Compact fact strip shown above the markets work surface in focus mode
// (Ship 1.3): risk / coverage / pilots one-liners so the broker keeps the
// deal context while working the ladder.
export function MarketsFactStrip({ deal, fleetAndRoster }) {
  const fleet = (fleetAndRoster?.fleet || []).filter((a) => a.status !== 'historical');
  const roster = (fleetAndRoster?.roster || []).filter((p) => p.status !== 'historical');
  const ac = fleet[0] || (deal?.aircraft || [])[0] || null;
  const tail = ac ? formatTail(ac.tail_number || ac.tail_display) : null;
  const risk = ac
    ? [tail, aircraftLabel(ac, ''), money(ac.hull_value ?? ac.current_hull_value)]
        .filter(Boolean).join(' · ')
    : '—';
  const occ = money(deal?.liability_per_occurrence);
  const pax = money(deal?.liability_per_passenger);
  const hull = deal?.coverage_type || (deal?.physical_damage ? 'Full Flight' : null);
  const eff = formatHumanDate(deal?.proposed_effective) || formatHumanDate(deal?.policy_start) || formatHumanDate(deal?.policy_expiration);
  const coverage = [occ ? `${occ}${pax ? ` / ${pax}` : ''}` : null, hull, eff ? `eff ${eff}` : null].filter(Boolean).join(' · ') || '—';
  // Never truncate silently: show up to 4 names, then "+N more" so the roster
  // size is always derivable from the cell (a 5-pilot deal must not read as 4).
  const pilotNames = roster.map((p) => p.name).filter(Boolean);
  const pilots = pilotNames.length
    ? (pilotNames.length > 4
        ? `${pilotNames.slice(0, 4).join(' · ')} +${pilotNames.length - 4} more`
        : pilotNames.join(' · '))
    : '—';
  const cells = [['Risk', risk], ['Coverage', coverage], ['Pilots', pilots]];
  return (
    <div className="mb-3 flex flex-wrap overflow-hidden rounded-card border border-border bg-bg">
      {cells.map(([k, v], i) => (
        <div key={k} className={`min-w-[180px] flex-1 px-3 py-2 ${i < cells.length - 1 ? 'border-r border-border' : ''}`}>
          <div className="text-[10px] uppercase tracking-[0.06em] text-text-dim">{k}</div>
          <div className="mt-0.5 text-[12px] font-medium text-text">{v}</div>
        </div>
      ))}
    </div>
  );
}

export function CoverageTargetsCard({ deal, onEdit }) {
  const occ = money(deal?.liability_per_occurrence);
  const pax = money(deal?.liability_per_passenger);
  const liability = occ
    ? `${occ} occurrence${pax ? ` / ${pax} passenger` : ''}${deal?.liability_smooth ? ' · smooth' : ''}`
    : null;
  const medical = money(deal?.medical_payments);
  const hull = deal?.coverage_type || (deal?.physical_damage ? 'Full Flight' : null);
  const effective =
    formatHumanDate(deal?.proposed_effective) ||
    formatHumanDate(deal?.policy_start) ||
    formatHumanDate(deal?.policy_expiration);
  const anything = liability || medical || hull;
  return (
    <CockpitCard title="Coverage targets" onEdit={onEdit}>
      {anything ? (
        <>
          <Row k="Liability" v={liability} />
          <Row k="Medical" v={medical} />
          <Row k="Hull" v={hull} />
          <Row k="Effective" v={effective} />
        </>
      ) : (
        <p className="text-[12px] italic text-text-muted">Coverage targets not set yet.</p>
      )}
    </CockpitCard>
  );
}
